import useSWR from "swr";
import { fetcher } from "@/utils";

export type HeartbeatSummary = {
  windowDays: number;
  inflowHex: number;
  outflowHex: number;
  penaltiesHex: number;
  inflowUsd: number;
  outflowUsd: number;
  penaltiesUsd: number;
};

export type HeartbeatDailyRow = {
  name: number;
  date: string;
  inflow: number;
  outflow: number;
  stakedSupplyChange: number;
  penalties: number;
  inflowUsd: number;
  outflowUsd: number;
  stakedSupplyChangeUsd: number;
  penaltiesUsd: number;
};

export type HeartbeatData = {
  meta: {
    scope: "combined";
    chains: ["ethereum", "pulsechain"];
    firstDay: number | null;
    lastDay: number | null;
    rowCount: number;
  };
  summary: HeartbeatSummary;
  data: HeartbeatDailyRow[];
};

/** @param days 90 for homepage preview; 0 or "all" for full history */
export function useHeartbeat(shouldFetch = true, days: number | "all" = 90) {
  const daysParam = days === "all" ? 0 : days;
  return useSWR<HeartbeatData>(
    shouldFetch ? `/api/heartbeat?days=${daysParam}&summaryDays=30` : null,
    fetcher,
    { refreshInterval: 300_000 },
  );
}

export function heartbeatRowsForUnit(
  rows: HeartbeatDailyRow[],
  unit: "hex" | "usd",
  options?: { signedOutflow?: boolean },
) {
  const signedOutflow = options?.signedOutflow ?? false;

  if (unit === "hex") {
    return rows.map((row) => {
      const outflow = signedOutflow ? -row.outflow : row.outflow;
      return {
        name: row.name,
        date: row.date,
        inflow: row.inflow,
        outflow,
        stakedSupplyChange: signedOutflow
          ? row.inflow + outflow
          : row.stakedSupplyChange,
        penalties: row.penalties,
      };
    });
  }

  return rows.map((row) => {
    const outflow = signedOutflow ? -row.outflowUsd : row.outflowUsd;
    return {
      name: row.name,
      date: row.date,
      inflow: row.inflowUsd,
      outflow,
      stakedSupplyChange: signedOutflow
        ? row.inflowUsd + outflow
        : row.stakedSupplyChangeUsd,
      penalties: row.penaltiesUsd,
    };
  });
}
